import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useKiosk } from '../context/KioskContext';
import { isLocalizablePath, localizedPath, stripLang } from '../utils/langPath';

const Header = ({ title, showBack, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, toggleLanguage, t, lp } = useLanguage();
  const { isKiosk } = useKiosk();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const [navTop, setNavTop] = useState(64);

  useEffect(() => {
    const measure = () => {
      if (headerRef.current) setNavTop(headerRef.current.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const isActive = (path) => stripLang(location.pathname) === path;

  const handleNavClick = (path) => {
    setMenuOpen(false);
    navigate(lp(path) + location.search);
  };

  // On pages that have both language URLs, switching language also switches the URL
  const handleLanguageSwitch = () => {
    const next = language === 'uk' ? 'ru' : 'uk';
    if (isLocalizablePath(location.pathname)) {
      setLanguage(next);
      navigate(localizedPath(location.pathname, next) + location.search, { state: location.state });
    } else {
      toggleLanguage();
    }
  };

  return (
    <header
      ref={headerRef}
      className={`bg-dewalt relative z-[100] transition-shadow duration-300 ${menuOpen ? '' : 'shadow-[0_2px_8px_rgba(0,0,0,0.15)]'}`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex items-center justify-between px-4 py-3 min-h-[56px] relative">
        {/* Left: back button or hamburger */}
        {showBack ? (
          <button
            className="bg-transparent border-none cursor-pointer p-2 -ml-2 rounded-lg flex items-center justify-center gap-1.5 text-black transition-all duration-200 hover:bg-black/10 active:scale-95"
            onClick={handleBack}
            aria-label="Go back"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <button
            className="bg-transparent border-none cursor-pointer p-2 -ml-2 rounded-lg flex flex-col items-center justify-center w-10 h-10 gap-[5px] text-black transition-all duration-200 hover:bg-black/10 active:scale-95"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span
              className="block w-[22px] h-0.5 bg-black rounded-sm transition-all duration-300 origin-center"
              style={menuOpen ? { transform: 'rotate(45deg) translate(5px, 5px)' } : {}}
            ></span>
            <span
              className={`block w-[22px] h-0.5 bg-black rounded-sm transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`}
            ></span>
            <span
              className="block w-[22px] h-0.5 bg-black rounded-sm transition-all duration-300 origin-center"
              style={menuOpen ? { transform: 'rotate(-45deg) translate(5px, -5px)' } : {}}
            ></span>
          </button>
        )}

        {/* Center: page title (sub-pages) or logo (main page, mobile) */}
        {showBack ? (
          <div className="text-base font-bold text-black m-0 absolute left-1/2 -translate-x-1/2 text-center whitespace-nowrap max-w-[55%] truncate">
            {title || t('header.title')}
          </div>
        ) : (
          <>
            {/* Static site title on desktop */}
            <div className="text-lg font-semibold text-black m-0 absolute left-1/2 -translate-x-1/2 text-center whitespace-nowrap hidden lg:block">
              {title || t('header.title')}
            </div>
            {/* Logo centered on mobile (main page only) */}
            <div className="absolute left-1/2 -translate-x-1/2 lg:hidden">
              <img src="/images/dewalt-logo.png" alt="DeWALT" className="h-9 w-auto object-contain" />
            </div>
          </>
        )}

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Logo in right area: on desktop always; on mobile only for sub-pages */}
          <div className={`hidden md:block items-center ${showBack ? 'flex' : 'hidden lg:flex'}`}>
            <img src="/images/dewalt-logo.png" alt="DeWALT" className="h-8 w-auto object-contain" />
          </div>

          {/* Language toggle */}
          <button
            className="flex items-center gap-1 bg-black/10 border-none px-2.5 py-1.5 rounded-lg text-[13px] font-bold text-black cursor-pointer transition-all duration-200 hover:bg-black/20 active:scale-95"
            onClick={handleLanguageSwitch}
            aria-label="Switch language"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>{language === 'uk' ? 'RU' : 'UA'}</span>
          </button>
        </div>
      </div>

      {/* Navigation Drawer */}
      <nav style={{ top: navTop }} className={`fixed left-0 right-0 bottom-0 z-[99] ${menuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${menuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setMenuOpen(false)}
        ></div>

        {/* Drawer panel */}
        <div className={`absolute top-0 left-0 w-[280px] max-w-[80%] h-full bg-white shadow-[4px_0_20px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>

          {/* Main nav items */}
          <div className="flex-1 overflow-y-auto py-2">
            <p className="px-6 pt-3 pb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">{t('header.tools')}</p>

            {!isKiosk && (
              <button
                className={`flex items-center gap-3.5 w-full px-6 py-3.5 border-none bg-transparent text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 text-left hover:bg-gray-100 hover:text-black ${isActive('/') ? 'bg-[#fffbf0] text-black border-l-[3px] border-l-dewalt' : ''}`}
                onClick={() => handleNavClick('/')}
              >
                <svg className={`flex-shrink-0 opacity-70 ${isActive('/') ? 'opacity-100 text-dewalt' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('header.rental')}
              </button>
            )}


            <button
              className={`flex items-center gap-3.5 w-full px-6 py-3.5 border-none bg-transparent text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 text-left hover:bg-gray-100 hover:text-black ${isActive('/how-it-works') ? 'bg-[#fffbf0] text-black border-l-[3px] border-l-dewalt' : ''}`}
              onClick={() => handleNavClick('/how-it-works')}
            >
              <svg className={`flex-shrink-0 opacity-70 ${isActive('/how-it-works') ? 'opacity-100 text-dewalt' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {t('header.howItWorks')}
            </button>

            <button
              className={`flex items-center gap-3.5 w-full px-6 py-3.5 border-none bg-transparent text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 text-left hover:bg-gray-100 hover:text-black ${isActive('/about') ? 'bg-[#fffbf0] text-black border-l-[3px] border-l-dewalt' : ''}`}
              onClick={() => handleNavClick('/about')}
            >
              <svg className={`flex-shrink-0 opacity-70 ${isActive('/about') ? 'opacity-100 text-dewalt' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('header.about')}
            </button>

            <button
              className={`flex items-center gap-3.5 w-full px-6 py-3.5 border-none bg-transparent text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 text-left hover:bg-gray-100 hover:text-black ${isActive('/business') ? 'bg-[#fffbf0] text-black border-l-[3px] border-l-dewalt' : ''}`}
              onClick={() => handleNavClick('/business')}
            >
              <svg className={`flex-shrink-0 opacity-70 ${isActive('/business') ? 'opacity-100 text-dewalt' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('header.business')}
            </button>

            {/* Divider */}
            <div className="mx-6 my-2 border-t border-gray-100" />
            <p className="px-6 pb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">{t('header.terms')}</p>

            <button
              className={`flex items-center gap-3.5 w-full px-6 py-3.5 border-none bg-transparent text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 text-left hover:bg-gray-100 hover:text-black ${isActive('/terms') ? 'bg-[#fffbf0] text-black border-l-[3px] border-l-dewalt' : ''}`}
              onClick={() => handleNavClick('/terms')}
            >
              <svg className={`flex-shrink-0 opacity-70 ${isActive('/terms') ? 'opacity-100 text-dewalt' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('header.terms')}
            </button>

            <button
              className={`flex items-center gap-3.5 w-full px-6 py-3.5 border-none bg-transparent text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 text-left hover:bg-gray-100 hover:text-black ${isActive('/safety') ? 'bg-[#fffbf0] text-black border-l-[3px] border-l-dewalt' : ''}`}
              onClick={() => handleNavClick('/safety')}
            >
              <svg className={`flex-shrink-0 opacity-70 ${isActive('/safety') ? 'opacity-100 text-dewalt' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('header.safety')}
            </button>

            <button
              className={`flex items-center gap-3.5 w-full px-6 py-3.5 border-none bg-transparent text-base font-medium text-gray-700 cursor-pointer transition-all duration-200 text-left hover:bg-gray-100 hover:text-black ${isActive('/privacy') ? 'bg-[#fffbf0] text-black border-l-[3px] border-l-dewalt' : ''}`}
              onClick={() => handleNavClick('/privacy')}
            >
              <svg className={`flex-shrink-0 opacity-70 ${isActive('/privacy') ? 'opacity-100 text-dewalt' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('header.privacy')}
            </button>
          </div>

          {/* Drawer footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
            <p className="text-xs text-gray-400 m-0">kh-tools.com</p>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
